<template>
  <div class="ola-carousel-item" :class="{
    'ola-carousel-item-active': active,
    'ola-carousel-item-card': parent.type === 'card',
    'ola-carousel-item-in-stage': inStage,
    'ola-carousel-item-hover': hover,
    'ola-carousel-item-image': isImage,
    'ola-carousel-item-video': isVideo,
    'has-floating-figure': floatingFigure
  }" @mouseenter="handleMouseEnter" @mouseleave="handleMouseLeave">
    <div v-if="parent.type === 'card'" v-show="!active" class="ola-carousel-item-mask"></div>

    <!-- 图片内容 -->
    <img v-if="isImage" :src="source" :alt="alt" class="ola-carousel-item-image">

    <!-- 视频内容 -->
    <video v-else-if="isVideo" class="ola-carousel-item-video" :src="source" :controls="videoControls"
           :autoplay="videoAutoplay" :muted="videoMuted" :loop="videoLoop"></video>

    <!-- 中间内容区域容器 -->
    <div class="ola-carousel-item-content-container">
      <!-- 文字层 -->
      <div v-if="isImage || isVideo" class="ola-carousel-item-text-overlay" :class="{
        'ola-carousel-item-text-overlay-active': active && animationReady,
        'text-animation-single': textLines === 1,
        'text-animation-multiple': textLines > 1,
        'text-align-left': floatingFigure
      }">
        <div class="ola-carousel-item-text-container">
          <!-- 使用默认插槽作为文字内容 -->
          <slot></slot>
        </div>
      </div>
      <!-- 浮动人物图层 -->
      <div v-if="floatingFigure" class="ola-carousel-item-floating-figure" :class="{
        'ola-carousel-item-floating-figure-active': active && animationReady
      }">
        <img :src="floatingFigure" :alt="floatingFigureAlt" class="floating-figure-image">
      </div>
    </div>

    <!-- 默认内容插槽，仅在非图片/视频模式下使用 -->
    <template v-if="!isImage && !isVideo">
      <slot></slot>
    </template>
  </div>
</template>

<script>
export default {
  name: 'OlaCarouselItem',
  inject: ['carousel'],
  props: {
    // 媒体源（图片URL或视频URL）
    source: {
      type: String,
      default: ''
    },
    // 媒体类型（image, video, 或空为默认插槽内容）
    type: {
      type: String,
      default: '',
      validator: (value) => {
        return ['', 'image', 'video'].includes(value);
      }
    },
    // 图片alt属性
    alt: {
      type: String,
      default: 'Carousel image'
    },
    // 视频控制选项
    videoControls: {
      type: Boolean,
      default: false
    },
    videoAutoplay: {
      type: Boolean,
      default: false
    },
    videoMuted: {
      type: Boolean,
      default: true
    },
    videoLoop: {
      type: Boolean,
      default: true
    },
    // 文字层选项
    textLines: {
      type: Number,
      default: 1,
      validator: (value) => {
        return value > 0;
      }
    },
    // 是否显示文字动画
    showTextAnimation: {
      type: Boolean,
      default: true
    },
    // 浮动人物图
    floatingFigure: {
      type: String,
      default: ''
    },
    // 浮动人物图alt属性
    floatingFigureAlt: {
      type: String,
      default: 'Floating figure'
    }
  },
  data() {
    return {
      hover: false,
      translate: 0,
      scale: 1,
      active: false,
      inStage: false,
      animationReady: false,
      animationTimer: null
    };
  },
  computed: {
    parent() {
      return this.carousel;
    },
    isVertical() {
      return this.carousel.direction === 'vertical';
    },
    isImage() {
      return this.type === 'image' && this.source;
    },
    isVideo() {
      return this.type === 'video' && this.source;
    }
  },
  watch: {
    active(val) {
      if (val) {
        // 当轮播项变为活动状态时，重置动画状态
        this.resetAnimation();
      } else {
        // 当轮播项变为非活动状态时，确保动画状态重置
        this.animationReady = false;
        // 清除可能存在的定时器
        if (this.animationTimer) {
          clearTimeout(this.animationTimer);
          this.animationTimer = null;
        }
      }
    }
  },
  methods: {
    processIndex(index, activeIndex, length) {
      if (activeIndex === 0 && index === length - 1) {
        return -1;
      } else if (activeIndex === length - 1 && index === 0) {
        return length;
      } else if (index < activeIndex - 1 && activeIndex - index >= length / 2) {
        return length + 1;
      } else if (index > activeIndex + 1 && index - activeIndex >= length / 2) {
        return -2;
      }
      return index;
    },
    calcCardTranslate(index, activeIndex) {
      const parentWidth = this.parent.$el.offsetWidth;
      if (this.inStage) {
        return parentWidth * ((2 - this.CARD_SCALE) * (index - activeIndex) + 1) / 4;
      } else if (index < activeIndex) {
        return -(1 + this.CARD_SCALE) * parentWidth / 4;
      } else {
        return (3 + this.CARD_SCALE) * parentWidth / 4;
      }
    },
    calcTranslate(index, activeIndex, isVertical) {
      const distance = isVertical ? this.parent.$el.offsetHeight : this.parent.$el.offsetWidth;
      return distance * (index - activeIndex);
    },
    handleMouseEnter() {
      this.hover = true;
      this.parent.handleMouseEnter();
    },
    handleMouseLeave() {
      this.hover = false;
      this.parent.handleMouseLeave();
    },
    resetAnimation() {
      // 清除可能存在的定时器
      if (this.animationTimer) {
        clearTimeout(this.animationTimer);
        this.animationTimer = null;
      }

      // 先设置为false，强制重置动画状态
      this.animationReady = false;

      // 使用nextTick确保DOM更新后再启用动画
      this.$nextTick(() => {
        // 强制浏览器重排，确保CSS过渡能够正确触发
        void this.$el.offsetHeight;

        // 添加一个短暂延时，确保CSS过渡能够正确触发
        this.animationTimer = setTimeout(() => {
          this.animationReady = true;
        }, 100); // 增加延时时间，确保动画有足够时间准备
      });
    }
  },
  created() {
    this.parent.addItem(this);
  },
  mounted() {
    // 如果组件初始化时就是活动状态，则初始化动画
    if (this.active) {
      this.resetAnimation();
    }
  },
  beforeUnmount() {
    // 清除可能存在的定时器
    if (this.animationTimer) {
      clearTimeout(this.animationTimer);
      this.animationTimer = null;
    }
    this.parent.removeItem(this);
  }
};
</script>

<style>
.carousel-title {
  font-size: 48px;
  font-weight: 600;
  margin: 0;
  line-height: normal;
  color: #fff;
}

@media (max-width: 991px) {
  .carousel-title {
    font-size: 40px;
  }
}

@media (max-width: 767px) {
  .carousel-title {
    font-size: 32px;
    margin: 0 0 8px;
  }
}

@media (max-width: 576px) {
  .carousel-title {
    font-size: 26px;
    margin: 0 0 5px;
    line-height: 1.2;
  }
}

@media (max-width: 375px) {
  .carousel-title {
    font-size: 22px;
    line-height: 1.1;
  }
}

.carousel-title .anim {
  display: inline-block;
  animation: gradient-animation 8s ease infinite;
  background: radial-gradient(circle, #6AC8FF 8.17%, #FF52E9 43.75%, #FF1F59 77.4%);
  /* background: radial-gradient(circle, #009dff 11.46%, #6d38fb 51.04%, #ff5353 88.24%); */
  -webkit-background-clip: text;
  background-clip: text;
  background-size: 210% 150%;
  color: transparent;
}

@keyframes gradient-animation {
  0% {
    background-position: 0 50%;
  }

  10% {
    background-position: -10% 50%;
  }

  50% {
    background-position: 100% 50%;
  }

  90% {
    background-position: -10% 50%;
  }

  100% {
    background-position: 0 50%;
  }
}

.carousel-subtitle {
  font-size: 24px;
  margin: 30px 0 40px 0;
}

.carousel-input {
  /* margin-top: 20px; */
  display: inline-block;
  justify-content: space-between;
  /* padding-left: 20px; */
  height: 44px;
  border-radius: 50px;

  /* background: #FFF; */
}

.carousel-input .carousel-input-inner {
  display: flex;

  @media (max-width: 576px) {
    justify-content: center;
  }
}

.carousel-input input {
  font-size: 16px;
  color: #999;
  border: none;
}

.carousel-input .ola-btn {
  display: flex;
  width: 150px;
  padding: 10px 18px;
  justify-content: center;
  margin: 0 auto;
  align-items: center;
  border-radius: 50px;
  font-size: 16px;
  font-weight: 500;
  border: 1px solid #FF1F59;
  background: #FF1F59;
}

@media (max-width: 767px) {
  .carousel-subtitle {
    font-size: 16px;
  }
}

@media (max-width: 576px) {
  .carousel-subtitle {
    font-size: 14px;
    line-height: 1.3;
  }
}

@media (max-width: 375px) {
  .carousel-subtitle {
    font-size: 12px;
    line-height: 1.2;
  }
}

.ola-carousel-item {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: transform .4s ease;
  will-change: transform, opacity;
}

.ola-carousel-item-active {
  z-index: 2;
}

.ola-carousel-item-mask {
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  background-color: rgba(0, 0, 0, 0.35);
  transition: .3s;
  z-index: 1;
}

.ola-carousel-item-card {
  transform: translateX(50%) scale(0.83);
}

.ola-carousel-item-card.ola-carousel-item-in-stage {
  cursor: pointer;
  z-index: 1;
}

.ola-carousel-item-card.ola-carousel-item-in-stage:hover .ola-carousel-item-mask,
.ola-carousel-item-card.ola-carousel-item-hover .ola-carousel-item-mask {
  opacity: 0.12;
}

.ola-carousel-item-card.ola-carousel-item-active {
  transform: translateX(0) scale(1);
  z-index: 2;
}

.ola-carousel-item-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.ola-carousel-item-video {
  width: 100%;
  margin: 0 auto;
  height: 100%;
  object-fit: cover;
  display: block;
}

.ola-carousel-item-text-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 3;
  color: #fff;
  text-align: center;
  will-change: opacity, transform;

  @media (max-width: 576px) {
    position: relative;
    text-align: center;
  }
}

/* 中间内容区域容器 */
.ola-carousel-item-content-container {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  max-width: 1200px;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 3;


}

.ola-carousel-item-text-container {
  padding: 0;
  max-width: 1200px;
  width: 100%;
  backface-visibility: hidden;

  @media (max-width: 576px) {
    text-align: center;
  }
}

.ola-carousel-item-text-container .carousel-title {
  color: #333;
}

.ola-carousel-item-text-container .sub-line {
  font-size: 40px;

  @media (max-width: 576px) {
    font-size: 24px;
  }
}

.ola-carousel-item-text-container .carousel-subtitle {
  color: #666;
}

@media (max-width: 991px) {
  .ola-carousel-item-text-container {
    max-width: 90%;
    padding: 0 20px;
  }
}

@media (max-width: 767px) {
  .ola-carousel-item-text-container {
    max-width: 600px;
    padding: 0 15px;
  }
}

@media (max-width: 576px) {
  .ola-carousel-item-text-container {
    max-width: 90%;
    padding: 0 10px;
  }
}

.text-animation-single .ola-carousel-item-text-container {
  opacity: 0;
  transform: translateY(20px);
  transition: opacity 0.8s ease-out, transform 0.8s ease-out;
  will-change: opacity, transform;
}

.text-animation-single.ola-carousel-item-text-overlay-active .ola-carousel-item-text-container {
  opacity: 1;
  transform: translateY(0);
}

.text-animation-multiple .ola-carousel-item-text-container>*:first-child {
  opacity: 0;
  transform: translateY(-20px);
  transition: opacity 0.8s ease-out, transform 0.8s ease-out;
  will-change: opacity, transform;
}

.text-animation-multiple .ola-carousel-item-text-container>*:nth-child(n+2) {
  opacity: 0;
  transform: translateY(20px);
  transition: opacity 0.8s ease-out 0.2s, transform 0.8s ease-out 0.2s;
  will-change: opacity, transform;
}

.text-animation-multiple.ola-carousel-item-text-overlay-active .ola-carousel-item-text-container>* {
  opacity: 1;
  transform: translateY(0);
}

/* 浮动人物图样式 */
.ola-carousel-item-floating-figure {
  position: absolute;
  right: -50px;
  top: 50%;
  transform: translateY(-50%) translateX(50px);
  z-index: 4;
  height: 70%;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  opacity: 0;
  transition: opacity 1s ease-out, transform 1s ease-out;
  will-change: opacity, transform;
  visibility: hidden;

  /* 初始状态完全隐藏 */
}

.ola-carousel-item-floating-figure-active {
  opacity: 1;
  transform: translateY(-50%) translateX(0);
  visibility: visible;
  /* 激活时显示 */
}

.floating-figure-image {
  height: 100%;
  max-height: 70vh;
  object-fit: contain;
  opacity: 0;
  /* 初始状态透明 */
  transition: opacity 0.3s ease-out;
}

.ola-carousel-item-floating-figure-active .floating-figure-image {
  opacity: 1;
  /* 激活时显示 */
}

/* 大屏幕优化 */
@media (min-width: 1600px) {
  .ola-carousel-item-floating-figure {
    height: 60%;
  }

  .floating-figure-image {
    max-height: 60vh;
  }
}

@media (min-width: 1920px) {
  .ola-carousel-item-floating-figure {
    height: 70%;
  }

  .floating-figure-image {
    max-height: 60vh;
  }
}



/* 高屏幕优化 */
@media (min-height: 1080px) {
  .ola-carousel-item-floating-figure {
    height: 55%;
  }

  .floating-figure-image {
    max-height: 55vh;
  }
}

@media (min-height: 1440px) {
  .ola-carousel-item-floating-figure {
    height: 60%;
  }

  .floating-figure-image {
    max-height: 60vh;
  }
}

/* 当有浮动人物图时，文字靠左对齐 */
.has-floating-figure .ola-carousel-item-text-overlay {
  justify-content: flex-start;
}

.text-align-left {
  text-align: left;
  justify-content: flex-start !important;
  padding-left: 0;
}

.text-align-left .ola-carousel-item-text-container {
  padding-left: 2%;
  width: 65%;
  max-width: 800px;
}

/* 移动端优化 */
@media (max-width: 1200px) {
  .ola-carousel-item-content-container {
    max-width: 90%;
  }

  .floating-figure-image {
    height: 80%;
  }

  .text-align-left .ola-carousel-item-text-container {
    width: 70%;
  }
}

/* 中等屏幕优化 (小于1025px) */
@media (max-width: 1024px) {
  .ola-carousel-item-content-container {
    max-width: 95%;
  }

  .ola-carousel-item-floating-figure {
    height: 75%;
  }

  .floating-figure-image {
    height: 100%;
    max-height: 75vh;
  }

  .text-align-left .ola-carousel-item-text-container {
    width: 72%;
    max-width: 600px;
  }

  .carousel-title {
    font-size: 42px;
    margin-bottom: 20px;
  }

  .carousel-subtitle {
    font-size: 22px;
    margin-bottom: 40px;
  }

  .carousel-input {
    width: 370px;
    height: 42px;
  }

  .carousel-input .ola-btn {
    width: 140px;
    font-size: 15px;
  }
}

/* 平板电脑优化 */
@media (max-width: 991px) {
  .floating-figure-image {
    height: 70%;
  }

  .text-align-left .ola-carousel-item-text-container {
    width: 75%;
  }

  .carousel-title {
    font-size: 36px;
    margin-bottom: 15px;
  }

  .carousel-subtitle {
    font-size: 20px;
    margin-bottom: 30px;
  }

  .carousel-input {
    width: 350px;
    height: 40px;
  }
}

/* 小平板/大手机优化 */
@media (max-width: 767px) {
  .floating-figure-image {
    height: 100%;
    right: -10%;
  }

  .ola-carousel-item-floating-figure {
    right: -5%;
    height: 70%;
  }

  .text-align-left .ola-carousel-item-text-container {
    width: 80%;
  }

  .carousel-title {
    font-size: 28px;
    margin-bottom: 10px;
  }

  .carousel-subtitle {
    font-size: 16px;
    margin-bottom: 20px;
  }

  .carousel-input {
    width: 300px;
    height: 38px;
  }

  .carousel-input .ola-btn {
    width: 120px;
    padding: 8px 15px;
    font-size: 14px;
  }
}

/* 手机端优化 */
@media (max-width: 576px) {
  .ola-carousel-item-content-container {
    flex-direction: column-reverse;
    justify-content: center;
  }

  .ola-carousel-item-floating-figure {
    position: relative;
    top: auto;
    right: auto;
    transform: none;
    height: 40%;
    width: 100%;
    z-index: 2;
    margin-bottom: 10px;
  }

  .ola-carousel-item-floating-figure-active {
    transform: translateY(0) translateX(0);
    opacity: 1;
  }

  .floating-figure-image {
    height: 100%;
    width: 100%;
    object-fit: contain;
    object-position: bottom center;
  }

  .ola-carousel-item-text-overlay {
    position: relative;
    width: 100%;
    height: auto;
    z-index: 3;
    text-align: center;
    align-items: center;
    justify-content: center;
    padding: 15px 0;
  }

  .text-align-left {
    text-align: center;
    justify-content: center !important;
    padding-left: 0;
  }

  .text-align-left .ola-carousel-item-text-container {
    width: 100%;
    padding: 0 10px;
    text-align: center;
  }

  .text-animation-single .ola-carousel-item-text-container {
    transform: translateY(10px);
  }

  .text-animation-multiple .ola-carousel-item-text-container>*:first-child {
    transform: translateY(-10px);
  }

  .text-animation-multiple .ola-carousel-item-text-container>*:nth-child(n+2) {
    transform: translateY(10px);
  }

  .carousel-title {
    font-size: 24px;
    margin-bottom: 8px;
  }

  .carousel-subtitle {
    font-size: 14px;
    margin-bottom: 15px;
  }

  .carousel-input {
    width: 100%;
    max-width: 280px;
    height: 36px;
  }

  .carousel-input .ola-btn {
    width: 100px;
    padding: 6px 12px;
    height: 30px;
    font-size: 12px;
  }
}
@media (min-width: 765px){
  .carousel-input .ola-btn{
    margin:0;
  }
}
/* 超小屏幕优化 */
@media (max-width: 375px) {
  .ola-carousel-item-floating-figure {
    height: 35%;
  }

  .carousel-title {
    font-size: 20px;
    margin-bottom: 5px;
  }

  .carousel-subtitle {
    font-size: 12px;
    margin-bottom: 10px;
  }

  .carousel-input {
    max-width: 250px;
    height: 34px;
  }

  .carousel-input input {
    font-size: 12px;
  }

  .carousel-input .ola-btn {
    width: 90px;
    padding: 5px 10px;
    font-size: 12px;
  }
}
</style>